import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Mail, Phone } from "lucide-react";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminInquiries() {
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c] = await Promise.all([
        axios.get(`${API}/quotes`),
        axios.get(`${API}/contact`),
      ]);
      setQuotes(q.data || []);
      setContacts(c.data || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const renderBody = () => {
    if (loading) {
      return (
        <div className="flex items-center gap-3 text-[#475569]">
          <Loader2 className="animate-spin" size={18} /> Loading…
        </div>
      );
    }
    if (tab === "quotes") return <QuotesList quotes={quotes} />;
    return <ContactsList contacts={contacts} />;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-page">
      <header className="bg-white border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo variant="dark" />
            <span className="text-xs font-mono text-[#94A3B8] tracking-widest uppercase">
              Inquiries Dashboard
            </span>
          </div>
          <a href="/" className="text-sm text-[#B85C38] hover:text-[#9B5633] font-medium" data-testid="admin-back-home">
            ← Back to site
          </a>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="flex items-end justify-between mb-10">
          <div>
            <h1 className="text-4xl font-medium tracking-tight text-[#0F172A]" style={{ fontFamily: "Outfit, sans-serif" }}>
              Customer Inquiries
            </h1>
            <p className="text-[#475569] mt-2">Quote requests &amp; contact messages from the website.</p>
          </div>
          <button onClick={load} className="text-sm text-[#475569] hover:text-[#B85C38]" data-testid="admin-refresh-button">
            Refresh
          </button>
        </div>

        <div className="flex border-b border-[#E2E8F0] mb-8">
          <TabButton active={tab === "quotes"} onClick={() => setTab("quotes")} count={quotes.length} label="Quote Requests" testid="admin-tab-quotes" />
          <TabButton active={tab === "contacts"} onClick={() => setTab("contacts")} count={contacts.length} label="Contact Messages" testid="admin-tab-contacts" />
        </div>

        {renderBody()}
      </div>
    </div>
  );
}

function TabButton({ active, onClick, count, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-6 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active ? "border-[#B85C38] text-[#0F172A]" : "border-transparent text-[#475569] hover:text-[#0F172A]"
      }`}
    >
      {label} <span className="font-mono text-xs ml-2">({count})</span>
    </button>
  );
}

function QuotesList({ quotes }) {
  if (!quotes.length) return <Empty message="No quote requests yet." />;
  return (
    <div className="space-y-4" data-testid="admin-quotes-list">
      {quotes.map((q) => (
        <div key={q.id} className="bg-white border border-[#E2E8F0] p-6 hover:shadow-sm transition-shadow" data-testid={`admin-quote-${q.id}`}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
            <div>
              <div className="text-lg font-medium text-[#0F172A]" style={{ fontFamily: "Outfit, sans-serif" }}>
                {q.full_name} <span className="text-[#475569]">· {q.company}</span>
              </div>
              <div className="font-mono text-xs text-[#94A3B8] mt-1">
                {new Date(q.created_at).toLocaleString()} · {q.country} {q.contractor_type ? `· ${q.contractor_type}` : ""}
              </div>
            </div>
            <div className="flex gap-3 text-sm">
              <a href={`mailto:${q.email}`} className="flex items-center gap-1.5 text-[#B85C38] hover:text-[#9B5633]"><Mail size={14} /> {q.email}</a>
              <a href={`tel:${q.phone}`} className="flex items-center gap-1.5 text-[#B85C38] hover:text-[#9B5633]"><Phone size={14} /> {q.phone}</a>
            </div>
          </div>
          {q.products?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {q.products.map((p) => (
                <span key={p} className="bg-[#F8F9FA] border border-[#E2E8F0] text-xs font-mono px-3 py-1.5">{p}</span>
              ))}
            </div>
          )}
          {q.quantity && <div className="text-sm text-[#475569] mb-2"><strong className="text-[#0F172A]">Quantity:</strong> {q.quantity}</div>}
          {q.message && <div className="text-sm text-[#475569] whitespace-pre-line bg-[#F8F9FA] p-4 border-l-2 border-[#B85C38]">{q.message}</div>}
        </div>
      ))}
    </div>
  );
}

function ContactsList({ contacts }) {
  if (!contacts.length) return <Empty message="No contact messages yet." />;
  return (
    <div className="space-y-4" data-testid="admin-contacts-list">
      {contacts.map((c) => (
        <div key={c.id} className="bg-white border border-[#E2E8F0] p-6" data-testid={`admin-contact-${c.id}`}>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-3">
            <div>
              <div className="text-lg font-medium text-[#0F172A]" style={{ fontFamily: "Outfit, sans-serif" }}>{c.name}</div>
              <div className="font-mono text-xs text-[#94A3B8] mt-1">{new Date(c.created_at).toLocaleString()}</div>
            </div>
            <a href={`mailto:${c.email}`} className="text-sm text-[#B85C38] hover:text-[#9B5633] flex items-center gap-1.5"><Mail size={14} /> {c.email}</a>
          </div>
          {c.subject && <div className="text-sm font-medium text-[#0F172A] mb-2">{c.subject}</div>}
          <div className="text-sm text-[#475569] whitespace-pre-line">{c.message}</div>
        </div>
      ))}
    </div>
  );
}

function Empty({ message }) {
  return (
    <div className="bg-white border border-dashed border-[#E2E8F0] p-16 text-center text-[#94A3B8]">
      {message}
    </div>
  );
}
